import { useState } from 'react';
import { ArrowLeftIcon, Eye, Save } from 'lucide-react';
import type { WidgetSlideContent } from '@r/helpers/widget-workspace-slides.ts';
import type { UseFormReturn } from 'react-hook-form';
import { Form } from '@r/components/ui/form.tsx';
import { Button } from '@r/components/ui/button.tsx';
import { Card, CardContent } from '@r/components/ui/card.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@r/components/ui/dialog.tsx';
import { ContentBlockWidgetPreviewDialog } from '@admin/components/personalization/content-block-widget-preview-dialog.tsx';
import { ContentBlockSchedulingFields } from '@admin/components/personalization/content-block-scheduling-fields.tsx';
import { ContentBlockSlideSwitch } from '@admin/components/personalization/content-block-slide-switch.tsx';
import type { WorkspaceScheduleState } from '@admin/pages/personalization/content-block-workspace-schedule.ts';
import { ContentBlockSlideFormFields } from '@admin/components/personalization/content-block-slide-form-fields.tsx';
import { EntitySegmentsCard } from '@admin/components/segments/entity-segments-card.tsx';
import type { WidgetWorkspaceWidgetWorkspaceReadModel } from '@r/api/generated/client.schemas.ts';
import type {
  ContentBlockEditorDialogFlags,
  ContentBlockEditorLoadState,
  ContentBlockEditorMode,
  ContentBlockEditorUploadState
} from '@admin/pages/personalization/use-content-block-editor.ts';
import type {
  ItemDefinition,
  SlideFormValues
} from '@admin/pages/personalization/content-block-slides.ts';
import type { z } from 'zod';

export interface ContentBlockEditorViewProps {
  loadState: ContentBlockEditorLoadState;
  t: (key: string, options?: Record<string, unknown>) => string;
  navigate: (options: { to: string }) => void;
  mode: ContentBlockEditorMode;
  selectedWorkspace: WidgetWorkspaceWidgetWorkspaceReadModel | undefined;
  form: UseFormReturn<SlideFormValues>;
  formSchema: z.ZodObject<Record<string, z.ZodTypeAny>>;
  itemDefinitions: ItemDefinition[];
  previewWidgetSlides: WidgetSlideContent[];
  activeSlide: number;
  slideCount: number;
  progresses: Record<string, number> | undefined;
  uploadState: ContentBlockEditorUploadState;
  schedule: WorkspaceScheduleState;
  handleScheduleChange: (schedule: WorkspaceScheduleState) => void;
  handleBackToHub: () => void;
  handleSlideChange: (slideIndex: number) => void;
  handleAddSlide: () => void;
  handleResetCurrentSlide: () => void;
  handleRequestRemoveSlide: (slideIndex: number) => void;
  onSubmit: (values: SlideFormValues) => Promise<void>;
  handleMediaUpload: (
    itemKey: string,
    files: File[],
    onChange: (value: string) => void
  ) => void | Promise<void>;
  dialogFlags: ContentBlockEditorDialogFlags;
  handleUnsavedWarningOpenChange: (open: boolean) => void;
  handleConfirmLeave: () => void;
  pendingSlideRemoval: number | null;
  handleSlideRemovalOpenChange: (open: boolean) => void;
  preventDialogDismissWhileSaving: (event: { preventDefault: () => void }) => void;
  handleConfirmRemoveSlide: () => void;
}

/** Vue de l’éditeur de bloc de contenu (formulaire, segments, dialogues). */
export const ContentBlockEditorView = ({
  loadState,
  t,
  navigate,
  mode,
  selectedWorkspace,
  form,
  formSchema,
  itemDefinitions,
  previewWidgetSlides,
  activeSlide,
  slideCount,
  progresses,
  uploadState,
  schedule,
  handleScheduleChange,
  handleBackToHub,
  handleSlideChange,
  handleAddSlide,
  handleResetCurrentSlide,
  handleRequestRemoveSlide,
  onSubmit,
  handleMediaUpload,
  dialogFlags,
  handleUnsavedWarningOpenChange,
  handleConfirmLeave,
  pendingSlideRemoval,
  handleSlideRemovalOpenChange,
  preventDialogDismissWhileSaving,
  handleConfirmRemoveSlide
}: ContentBlockEditorViewProps) => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const isFormBusy = uploadState.isUploading || uploadState.isMediaUploading;
  const canPreview = previewWidgetSlides.length > 0 && itemDefinitions.length > 0;
  if (loadState === 'loading') {
    return <div>{t('admin.contentblock.loading')}</div>;
  }

  if (loadState === 'error') {
    return <div>{t('admin.contentblock.error')}</div>;
  }

  if (loadState === 'not_found') {
    return (
      <div className="p-4 space-y-4">
        <p>{t('admin.contentblock.error')}</p>
        <Button
          type="button"
          rounded="full"
          onClick={() => void navigate({ to: '/content/content-block' })}
        >
          <ArrowLeftIcon className="w-4 h-4" aria-hidden />
          {t('admin.contentblock.back.hub.error')}
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className="py-2 space-y-6 w-full">
        <Button type="button" rounded="full" onClick={handleBackToHub}>
          <ArrowLeftIcon className="w-4 h-4" aria-hidden />
          {t('admin.contentblock.back.hub')}
        </Button>

        <Card className="w-full rounded-2xl shadow-md border-border/60 bg-card">
          <CardContent className="p-6 md:p-10">
            <ContentBlockSlideSwitch
              activeSlide={activeSlide}
              slideCount={slideCount}
              onSlideChange={handleSlideChange}
              onAddSlide={handleAddSlide}
              onRemoveSlide={handleRequestRemoveSlide}
            />

            <div className="flex flex-col gap-4 pb-6 mb-8 border-b border-border/40 md:flex-row md:items-start md:justify-between">
              <div className="space-y-2">
                <div className="flex flex-wrap gap-3 items-center">
                  <h1 className="text-2xl font-bold tracking-tight">
                    {t('admin.contentblock.editor.slide.title', {
                      slide: activeSlide
                    })}
                  </h1>
                  {activeSlide === 1 && (
                    <span className="inline-flex items-center rounded-full bg-primary/15 px-3 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary">
                      {t('admin.contentblock.editor.slide.primary')}
                    </span>
                  )}
                </div>
                <p className="max-w-2xl text-sm text-muted-foreground">
                  {t('admin.contentblock.editor.slide.subtitle')}
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                rounded="full"
                className="gap-2 shadow-sm shrink-0 border-border/70 bg-background"
                disabled={!canPreview || isFormBusy}
                onClick={() => setIsPreviewOpen(true)}
              >
                <Eye className="w-4 h-4" aria-hidden />
                {t('admin.contentblock.editor.preview')}
              </Button>
            </div>

            <Form {...form} schema={formSchema}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                <ContentBlockSlideFormFields
                  itemDefinitions={itemDefinitions}
                  form={form}
                  progresses={progresses}
                  mediaFieldDisabled={isFormBusy}
                  onMediaUpload={handleMediaUpload}
                />

                <ContentBlockSchedulingFields
                  schedule={schedule}
                  onChange={handleScheduleChange}
                  t={t}
                />

                <div className="flex flex-col gap-3 pt-8 border-t border-border/40 sm:flex-row sm:items-center">
                  <Button
                    rounded="full"
                    type="submit"
                    loading={form.formState.isSubmitting || isFormBusy}
                    className="flex-1 gap-2 h-12 text-base shadow-sm"
                  >
                    <Save className="w-4 h-4" />
                    {t('admin.contentblock.editor.save')}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    rounded="full"
                    className="px-8 h-12 shrink-0 border-border/70"
                    onClick={handleResetCurrentSlide}
                  >
                    {t('admin.contentblock.editor.reset')}
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>

        {mode === 'edit' && selectedWorkspace?.id && (
          <EntitySegmentsCard
            key={selectedWorkspace.id}
            entityType="widget"
            entityId={selectedWorkspace.id}
          />
        )}
      </div>

      <ContentBlockWidgetPreviewDialog
        open={isPreviewOpen}
        onOpenChange={setIsPreviewOpen}
        slides={previewWidgetSlides}
        t={t}
      />

      <Dialog open={dialogFlags.isUnsavedWarningOpen} onOpenChange={handleUnsavedWarningOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('admin.contentblock.unsaved.title')}</DialogTitle>
            <DialogDescription>{t('admin.contentblock.unsaved.description')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              rounded="full"
              onClick={() => handleUnsavedWarningOpenChange(false)}
            >
              {t('admin.contentblock.unsaved.stay')}
            </Button>
            <Button type="button" rounded="full" onClick={handleConfirmLeave}>
              {t('admin.contentblock.unsaved.leave')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={pendingSlideRemoval !== null} onOpenChange={handleSlideRemovalOpenChange}>
        <DialogContent
          className="max-w-md"
          onInteractOutside={preventDialogDismissWhileSaving}
          onEscapeKeyDown={preventDialogDismissWhileSaving}
        >
          <DialogHeader>
            <DialogTitle>{t('admin.contentblock.slide.delete.title')}</DialogTitle>
            <DialogDescription>
              {t('admin.contentblock.slide.delete.description')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              rounded="full"
              disabled={dialogFlags.isSlideRemovalSaving}
              onClick={() => handleSlideRemovalOpenChange(false)}
            >
              {t('admin.contentblock.confirm.no')}
            </Button>
            <Button
              type="button"
              variant="destructive"
              rounded="full"
              loading={dialogFlags.isSlideRemovalSaving}
              onClick={handleConfirmRemoveSlide}
            >
              {t('admin.contentblock.confirm.yes')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
